<template>
    <main class="timesheet-import">
        <header class="timesheet-import__header">
            <div>
                <b-button variant="link" class="p-0 mb-2" to="/timesheets">
                    <i class="fas fa-arrow-left mr-1" aria-hidden="true"></i>
                    К табелям
                </b-button>
                <h1>Перенос истории</h1>
                <b-button v-if="canResolveHistoryPeople" size="sm" variant="outline-secondary" to="/timesheets/history-people">Сопоставить сотрудников архива</b-button>
                <p>Табели, начисления, ставки и оценки из старой системы</p>
            </div>
        </header>

        <b-alert v-if="errorMessage" show dismissible variant="danger" @dismissed="errorMessage = ''">
            {{ errorMessage }}
        </b-alert>
        <b-alert v-if="successMessage" show dismissible variant="success" @dismissed="successMessage = ''">
            {{ successMessage }}
        </b-alert>
        <b-alert show variant="info">
            Проверка сохраняет подготовленный пакет и сопоставления. Рабочая история переносится после отдельного подтверждения.
            Полный месяц переносится целиком; повторное применение тех же данных не создаёт дубли.
        </b-alert>

        <TimesheetImportQueue ref="importQueue" :locked="loading || Boolean(mappingRowKey)" @busy="queueBusy = $event" @open="openQueueBatch" @clear="clearDetails" />

        <template v-if="batch">
            <section ref="batchDetails" class="timesheet-import__summary" tabindex="-1">
                <article>
                    <strong>{{ summary.total || 0 }}</strong>
                    <span>строк</span>
                </article>
                <article>
                    <strong>{{ summary.mapped || 0 }}</strong>
                    <span>сопоставлено</span>
                </article>
                <article :class="{'is-warning': summary.unresolved_employees}">
                    <strong>{{ summary.unresolved_employees || 0 }}</strong>
                    <span>строк без связи с сотрудником</span>
                </article>
                <article :class="{'is-danger': summary.unresolved_objects}">
                    <strong>{{ summary.unresolved_objects || 0 }}</strong>
                    <span>строк без объекта</span>
                </article>
            </section>

            <section class="timesheet-import__batch">
                <div>
                    <strong>{{ batch.file_name }}</strong>
                    <span>{{ batch.range_start || '—' }} — {{ batch.range_end || '—' }}</span>
                    <b-badge class="timesheet-status-badge" :variant="batch.status === 'applied' ? 'success' : 'secondary'">
                        {{ statusLabel }}
                    </b-badge>
                </div>
                <b-button size="sm" variant="link" :disabled="loading || queueBusy || Boolean(mappingRowKey)" @click="clearDetails">Закрыть подробности</b-button>
            </section>

            <section v-if="manifest" class="timesheet-import__manifest">
                <strong>{{ manifest.complete_month ? 'Полный месяц' : 'Пакет истории' }}: {{ manifest.period_start || batch.range_start }}</strong>
                <p class="mb-1">{{ compositionLabel }}</p>
                <p v-for="total in monetaryTotals" :key="total.currency" class="mb-1">
                    {{ total.currency }}: расчёт {{ money(total.calculated_amount_minor, total.currency) }} · итог {{ money(total.final_amount_minor, total.currency) }}
                    <small v-if="total.historical_calculation" class="d-block text-muted">Расчётная сумма отсутствует в {{ total.historical_calculation.unknown_source_count }} исходных строках; итог окончательных решений сохранён полностью.</small>
                </p>
                <ul v-if="manifestSources.length"><li v-for="(source, index) in manifestSources" :key="index">{{ manifestSourceLabel(source) }}</li></ul>
            </section>
            <b-alert v-if="blockingDiagnostics.length" show variant="danger">
                <strong>Перенос заблокирован</strong>
                <ul class="mb-0"><li v-for="(item, index) in blockingDiagnostics" :key="index">{{ diagnosticLabel(item) }}</li></ul>
            </b-alert>
            <b-alert v-if="importWarnings.length" show variant="warning">
                <details><summary>Замечания к исходным данным ({{ importWarnings.length }})</summary>
                    <ul class="mb-0"><li v-for="(item, index) in importWarnings" :key="index">{{ diagnosticLabel(item) }}</li></ul>
                </details>
            </b-alert>

            <b-alert v-if="blockingRows.length" show variant="warning">
                Нельзя применить пакет: сопоставьте все объекты и сотрудников в строках исторических ставок.
                Неразобранных людей из табелей и начислений можно перенести — они попадут на доску кадровиков.
            </b-alert>

            <b-form-checkbox v-if="batch.status !== 'applied'" v-model="spreadMappings" class="mt-3" :disabled="queueBusy || loading || Boolean(mappingRowKey)">
                Применять подтверждённое сопоставление ко всем выбранным месяцам с тем же ключом и именем источника
            </b-form-checkbox>
            <section class="timesheet-import__rows">
                <header>
                    <h2>Строки, требующие внимания</h2>
                    <b-form-input
                        v-model.trim="filter"
                        type="search"
                        placeholder="ФИО, объект или ключ строки"
                    ></b-form-input>
                </header>
                <p v-if="!attentionRows.length" class="timesheet-import__empty">
                    {{ filter ? 'По этому запросу строк нет.' : batch.status === 'applied' ? 'Пакет перенесён.' : 'Все обязательные соответствия определены.' }}
                </p>
                <article v-for="row in attentionRows" :key="row.id || row.row_key">
                    <div class="timesheet-import__row-title">
                        <div>
                            <strong>{{ employeeName(row) }}</strong>
                            <span>{{ typeLabel(row.record_type) }} · {{ objectName(row) }}</span>
                            <small v-if="row.employee_id">{{ mappingLabel(row, 'employee') }}</small>
                            <small v-if="row.building_id">{{ mappingLabel(row, 'object') }}</small>
                        </div>
                        <code>{{ row.row_key }}</code>
                    </div>

                    <div v-if="!row.employee_id" class="timesheet-import__mapping">
                        <span>Сотрудник</span>
                        <b-button
                            v-for="candidate in employeeCandidates(row)"
                            :key="candidate.employee_id"
                            size="sm"
                            variant="outline-primary"
                            :disabled="Boolean(mappingRowKey) || queueBusy || batch.status === 'applied'"
                            @click="mapEmployee(row, candidate)"
                        >
                            {{ candidate.full_name }}
                            <small v-if="candidate.personnel_number">№ {{ candidate.personnel_number }}</small>
                        </b-button>
                        <em v-if="!employeeCandidates(row).length">
                            Кандидатов нет — человек будет перенесён как неразобранный.
                        </em>
                    </div>

                    <div v-if="row.record_type === 'timesheet_line' && !row.building_id" class="timesheet-import__mapping">
                        <span>Объект</span>
                        <b-button
                            v-for="candidate in objectCandidates(row)"
                            :key="candidate.building_id"
                            size="sm"
                            variant="outline-primary"
                            :disabled="Boolean(mappingRowKey) || queueBusy || batch.status === 'applied'"
                            @click="mapObject(row, candidate)"
                        >
                            {{ candidate.name }}
                        </b-button>
                        <em v-if="!objectCandidates(row).length">
                            Автоподбор не нашёл кандидатов.
                        </em>
                        <b-form-select
                            :value="objectSelections[row.row_key]"
                            :options="objectOptions"
                            size="sm"
                            :disabled="Boolean(mappingRowKey) || queueBusy || batch.status === 'applied'"
                            @input="value => setObjectSelection(row.row_key, value)"
                        ></b-form-select>
                        <b-button
                            size="sm"
                            variant="primary"
                            :disabled="!objectSelections[row.row_key] || Boolean(mappingRowKey) || queueBusy || batch.status === 'applied'"
                            @click="mapSelectedObject(row)"
                        >
                            Сопоставить
                        </b-button>
                    </div>
                </article>
            </section>
        </template>
    </main>
</template>

<script>
import api from '@/utils/api';
import TimesheetImportQueue from './TimesheetImportQueue.vue';

export default {
    name: 'TimesheetHistoryImport',
    components: {TimesheetImportQueue},
    data() {
        return {
            queueBusy: false,
            spreadMappings: true,
            canResolveHistoryPeople: false,
            batch: null,
            summary: {},
            rows: [],
            filter: '',
            loading: false,
            mappingRowKey: null,
            objects: [],
            objectSelections: {},
            errorMessage: '',
            successMessage: ''
        };
    },
    computed: {
        manifest() { return this.batch && this.batch.manifest || this.summary.manifest || null; },
        manifestSources() { return this.manifest && (this.manifest.source_files || this.manifest.sources) || []; },
        compositionLabel() {
            const counts = this.summary.by_type || this.manifest && this.manifest.record_counts || {};
            return Object.keys(counts).map(key => `${this.typeLabel(key)}: ${counts[key]}`).join(' · ');
        },
        monetaryTotals() { return Object.keys(this.summary.totals_by_currency || {}).map(currency => ({
            currency, ...this.summary.totals_by_currency[currency]
        })); },
        blockingDiagnostics() { return this.summary.diagnostics && this.summary.diagnostics.blocking || []; },
        importWarnings() {
            const warnings = this.summary.diagnostics && this.summary.diagnostics.warnings;
            return Array.isArray(warnings) && warnings.length ? warnings : this.batch && this.batch.warnings || [];
        },
        objectOptions() {
            return [{value: null, text: 'Выберите объект Argus'}].concat(this.objects.map(object => ({
                value: Number(object.building_id),
                text: object.name
            })));
        },
        statusLabel() {
            const labels = {
                previewed: 'Проверен',
                applying: 'Переносится',
                applied: 'Перенесён',
                failed: 'Ошибка',
                cancelled: 'Отменён'
            };
            return labels[this.batch && this.batch.status] || 'Пакет';
        },
        blockingRows() {
            return this.rows.filter(row => (
                (row.record_type === 'timesheet_line' && !row.building_id)
                || (row.record_type === 'compensation_term' && !row.employee_id)
            ));
        },
        attentionRows() {
            const query = this.filter.toLocaleLowerCase('ru-RU');
            return this.rows.filter(row => (
                !row.employee_id || (row.record_type === 'timesheet_line' && !row.building_id)
            )).filter(row => {
                if (!query) return true;
                return [
                    row.row_key,
                    this.employeeName(row),
                    this.objectName(row),
                    this.typeLabel(row.record_type)
                ].some(value => String(value || '').toLocaleLowerCase('ru-RU').includes(query));
            });
        }
    },
    beforeRouteLeave(to, from, next) {
        if (this.queueBusy || this.loading || this.mappingRowKey) {
            this.errorMessage = 'Остановите очередь и дождитесь текущего запроса перед уходом со страницы.';
            next(false);
        } else next();
    },
    async mounted() {
        try {
            const response = await api.get('/api/timesheets/bootstrap', {ignoreModal: true});
            this.objects = response.data.objects || [];
            this.canResolveHistoryPeople = !!(response.data.permissions && response.data.permissions.can_resolve_history_people);
        } catch (error) {
            this.errorMessage = this.responseMessage(error, 'Не удалось загрузить список объектов.');
        }
    },
    methods: {
        money(value, currency) { return value === null || value === undefined ? '—'
            : new Intl.NumberFormat('ru-RU', {style: 'currency', currency}).format(Number(value) / 100); },
        diagnosticLabel(value) { return typeof value === 'string' ? value : value.message || value.text || value.code || 'Замечание к источнику'; },
        manifestSourceLabel(value) {
            return typeof value === 'string' ? value : [value.name || value.sheet_name || value.source_file_name || value.id || value.spreadsheet_id,
                value.row_count === undefined ? null : `${value.row_count} строк`].filter(Boolean).join(' · ');
        },
        mappingLabel(row, type) {
            const method = row.match_details && row.match_details[type] && row.match_details[type].method;
            return method === 'confirmed_mapping' ? 'Соответствие подтверждено'
                : method === 'singleton_google_profile' ? 'Связь из сохранённого профиля источника' : 'Предложенное соответствие';
        },
        responseMessage(error, fallback) {
            return error && error.response && error.response.data && error.response.data.msg
                ? error.response.data.msg
                : fallback;
        },
        setBatch(payload) {
            this.batch = payload.batch || null;
            this.summary = payload.summary || (payload.batch && payload.batch.summary) || {};
            this.rows = Array.isArray(payload.rows) ? payload.rows : [];
            this.$refs.importQueue.acceptDetails(payload);
        },
        clearDetails() { this.batch = null; this.summary = {}; this.rows = []; this.objectSelections = {}; this.filter = ''; },
        async openQueueBatch(id) {
            if (this.queueBusy || this.loading || this.mappingRowKey) return;
            this.loading = true; this.errorMessage = ''; this.clearDetails();
            try {
                const {data} = await api.get(`/api/timesheets/imports/${id}`, {ignoreModal: true});
                this.setBatch(data);
                await this.$nextTick();
                this.$refs.batchDetails.focus({preventScroll: true});
                this.$refs.batchDetails.scrollIntoView({block: 'start'});
            } catch (error) { this.errorMessage = this.responseMessage(error, 'Не удалось открыть пакет.'); }
            finally { this.loading = false; }
        },
        employeeName(row) {
            return row.normalized_payload && row.normalized_payload.employee
                ? row.normalized_payload.employee.full_name
                : 'Человек не указан';
        },
        objectName(row) {
            return row.normalized_payload && row.normalized_payload.object
                ? row.normalized_payload.object.name
                : 'без объекта';
        },
        typeLabel(type) {
            return {
                timesheet_line: 'табель',
                payroll_line: 'начисление',
                compensation_term: 'ставка',
                historical_assessment: 'оценка ИТР',
                historical_payroll_input: 'ведомость без решения о начислении'
            }[type] || type;
        },
        employeeCandidates(row) {
            return row.match_details && row.match_details.employee
                && Array.isArray(row.match_details.employee.candidates)
                ? row.match_details.employee.candidates
                : [];
        },
        objectCandidates(row) {
            return row.match_details && row.match_details.object
                && Array.isArray(row.match_details.object.candidates)
                ? row.match_details.object.candidates
                : [];
        },
        async mapEmployee(row, candidate) {
            await this.saveMapping(row, {
                mapping_type: 'employee',
                source_key: row.normalized_payload.employee.source_key,
                source_label: row.normalized_payload.employee.full_name,
                employee_id: candidate.employee_id
            });
        },
        async mapObject(row, candidate) {
            await this.saveMapping(row, {
                mapping_type: 'object',
                source_key: row.normalized_payload.object.source_key,
                source_label: row.normalized_payload.object.name,
                building_id: candidate.building_id
            });
        },
        async mapSelectedObject(row) {
            const buildingId = Number(this.objectSelections[row.row_key]);
            const candidate = this.objects.find(object => Number(object.building_id) === buildingId);
            if (!candidate) return;
            await this.mapObject(row, candidate);
        },
        setObjectSelection(rowKey, value) {
            this.$set(this.objectSelections, rowKey, value);
        },
        async saveMapping(row, payload) {
            if (this.queueBusy || this.loading || this.mappingRowKey || !this.batch || this.batch.status === 'applied') return;
            const batchId = this.batch.id;
            this.mappingRowKey = row.row_key;
            this.errorMessage = '';
            try {
                await api.put(`/api/timesheets/imports/${batchId}/mappings`, payload);
                if (this.spreadMappings) await this.$refs.importQueue.spreadMapping(payload, batchId);
                const refreshed = await api.get(`/api/timesheets/imports/${batchId}`);
                this.setBatch(refreshed.data);
            } catch (error) {
                this.errorMessage = this.responseMessage(error, 'Не удалось сохранить сопоставление.');
            } finally {
                this.mappingRowKey = null;
            }
        },
    }
};
</script>

<style scoped>
.timesheet-import {
    max-width: 1180px;
    margin: 0 auto;
    padding: 24px;
}

.timesheet-import__header h1,
.timesheet-import__rows h2 {
    margin: 0;
}

.timesheet-import__header p,
.timesheet-import__batch span:not(.badge),
.timesheet-import__row-title span {
    display: block;
    color: #6c757d;
}

.timesheet-import__upload,
.timesheet-import__batch,
.timesheet-import__manifest,
.timesheet-import__rows {
    margin-top: 20px;
    padding: 18px;
    border: 1px solid #dee2e6;
    border-radius: 12px;
    background: #fff;
}

.timesheet-import__upload,
.timesheet-import__batch,
.timesheet-import__rows > header,
.timesheet-import__row-title,
.timesheet-import__mapping {
    display: flex;
    align-items: center;
    gap: 12px;
}

.timesheet-import__upload label,
.timesheet-import__rows > header .form-control {
    flex: 1;
}

.timesheet-import__upload label > span,
.timesheet-import__mapping > span {
    display: block;
    margin-bottom: 6px;
    font-weight: 600;
}

.timesheet-import__summary {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin-top: 20px;
}

.timesheet-import__summary article {
    padding: 16px;
    border-radius: 12px;
    background: #f1f3f5;
}

.timesheet-import__summary strong,
.timesheet-import__summary span {
    display: block;
}

.timesheet-import__summary strong {
    font-size: 1.6rem;
}

.timesheet-import__summary .is-warning {
    background: #fff3cd;
}

.timesheet-import__summary .is-danger {
    background: #f8d7da;
}

.timesheet-import__batch,
.timesheet-import__row-title {
    justify-content: space-between;
}

.timesheet-import__rows article {
    padding: 16px 0;
    border-top: 1px solid #e9ecef;
}

.timesheet-import__rows article:first-of-type {
    margin-top: 16px;
}

.timesheet-import__row-title code {
    color: #495057;
    overflow-wrap: anywhere;
}

.timesheet-import__mapping {
    flex-wrap: wrap;
    margin-top: 12px;
}

.timesheet-import__mapping > span {
    min-width: 90px;
    margin: 0;
}

.timesheet-import__mapping em,
.timesheet-import__empty {
    color: #6c757d;
}

.timesheet-import__mapping .custom-select {
    max-width: 320px;
}

@media (max-width: 767px) {
    .timesheet-import {
        padding: 14px;
    }

    .timesheet-import__upload,
    .timesheet-import__batch,
    .timesheet-import__rows > header {
        align-items: stretch;
        flex-direction: column;
    }

    .timesheet-import__summary {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .timesheet-import__row-title {
        align-items: flex-start;
        flex-direction: column;
    }
}
</style>

<style scoped src="./timesheet-status-badges.css"></style>
